/**
 * A business's website, read in setup or later beside its files: what the
 * reader takes from it, the shapes of its routes, and the small rules both ends
 * hold to.
 *
 * Decided 23 to 31 of docs/plans/setup-from-files.md. A website is a row of
 * `app.agent_file` with kind `website`; code reads the pages and the model reads
 * this summary, never the pages themselves.
 */
import type { AgentFile, FileOrigin } from './agent-files';
import type { LeftOut } from './setup-sources';

/** Up to 30 pages of one site are read (decision 10). */
export const WEBSITE_MAX_PAGES = 30;

/**
 * Why a website (or a page, or a place) could not be used. One line each in
 * the UI. `blocked` keeps readers out: its robots.txt, or an HTTP 403, 429 or
 * 451. `sign_in` asks for a sign-in, `private` is shared with no one, `gone` is
 * no longer there, `parked` is a domain with no site on it, `not_business` is
 * not a business's page.
 */
export const WEBSITE_FAILURES = ['unreachable', 'blocked', 'thin', 'not_html', 'too_big', 'sign_in', 'private', 'gone', 'parked', 'not_business'] as const;
export type WebsiteFailure = (typeof WEBSITE_FAILURES)[number];

/** What a page is for, judged by its address and its words. */
export const WEBSITE_PAGE_KINDS = ['home', 'about', 'products', 'services', 'prices', 'menu', 'contact', 'hours', 'branches', 'other'] as const;
export type WebsitePageKind = (typeof WEBSITE_PAGE_KINDS)[number];

/** One page read, as the person sees it in the table of what each page gave. */
export type WebsitePage = {
  readonly url: string;
  readonly title: string;
  readonly kind: WebsitePageKind;
  /** One line of what the page gave, for example "24 items with prices". */
  readonly gave: string;
  /** Characters of plain text kept from the page. */
  readonly chars: number;
};

/** What the whole site gave. Every list is short: the model reads this. */
export type WebsiteFound = {
  readonly name: string | null;
  readonly about: string | null;
  readonly phones: readonly string[];
  readonly emails: readonly string[];
  readonly addresses: readonly string[];
  readonly hours: readonly string[];
  readonly gstin: string | null;
  /** Items or services with a price when the page gave one, at most 500. */
  readonly items: readonly { readonly name: string; readonly price: string | null }[];
  readonly itemCount: number;
};

export type WebsiteSummary = {
  readonly kind: 'website';
  readonly url: string;
  readonly host: string;
  /** While reading: the pages done of the pages chosen, and the page being read. */
  readonly progress: { readonly done: number; readonly of: number; readonly current: string | null } | null;
  readonly pages: readonly WebsitePage[];
  /** Pages seen but not read, with why (robots, another site, a form, past the limit). */
  readonly leftOut: readonly { readonly url: string; readonly why: 'robots' | 'other_site' | 'form' | 'limit' }[];
  readonly found: WebsiteFound;
  /** True when the model read the pages itself because the code found too little text. */
  readonly readByModel: boolean;
  /** A marketplace or social page: one public page only, used after the person says so. */
  readonly marketplace: boolean;
  readonly readAt: string;
  /**
   * A map place's lookup (step 4.11): Google's Search Suggestions, exactly as
   * served, for the Looked up chip. Absent on a site read page by page.
   */
  readonly suggestions?: string | null;
  /** Set when the site was found in a file (Decided 28): which file, and where in it. */
  readonly foundIn?: { readonly fileId: string; readonly fileName: string; readonly place: string };
  /** What the pages' text had masked (ID numbers, phones not the business's), by kind. */
  readonly redacted?: readonly LeftOut[];
};

/** A website address found inside a file, and what OS makes of it. */
export type WebsiteFoundInFile = {
  readonly url: string;
  readonly host: string;
  readonly foundIn: { readonly fileId: string; readonly fileName: string; readonly place: string };
  /** What matched on the home page, for example "the phone matches your invoice". */
  readonly evidence: readonly string[];
  /** The decision model's probability that this is the business's own site. */
  readonly sure: number;
  /** `read` at 0.8 or above with evidence (the reader has started); `ask` otherwise. */
  readonly state: 'read' | 'ask';
  /** For `read`: the website's own row, kept and being read, to poll with GET /v1/files/:id. */
  readonly websiteId?: string;
};

/** One change Read again found. Nothing changes until the person chooses. */
export type WebsiteChange = {
  readonly about: string;
  readonly was: string | null;
  readonly now: string | null;
};

/* ------------------------------------------------------------------ routes */

export type WebsiteCreateRequest = {
  readonly url: string;
  readonly origin: FileOrigin;
  readonly originRef?: string;
  /** Set when the person said yes to an address OS found in a file. */
  readonly foundIn?: { readonly fileId: string; readonly place: string };
};
export type WebsiteCreateResponse = { readonly file: AgentFile };

/**
 * POST /v1/websites refused: what was typed is not a website. A phone number or
 * a handle is never read (Decided 27): `kept` says which it was and `fact` the
 * words it was kept as (in the workspace's facts when there is one; during
 * setup the web keeps them with the setup).
 */
export type WebsiteNotASite = {
  readonly error: {
    readonly code: 'not_a_website';
    readonly message: string;
    readonly kept: 'phone' | 'handle' | null;
    readonly fact: string | null;
  };
};

export type WebsiteAgainResponse = { readonly changes: readonly WebsiteChange[]; readonly fresh: WebsiteSummary };
export type WebsiteApplyRequest = { readonly use: boolean };

/* ------------------------------------------------------------------- rules */

/** Hosts read as one public page only, and used only after the person says so. */
export const MARKETPLACE_HOSTS = [
  'indiamart.com',
  'justdial.com',
  'tradeindia.com',
  'amazon.in',
  'amazon.com',
  'flipkart.com',
  'meesho.com',
  'facebook.com',
  'instagram.com',
  'maps.google.com',
  'g.page',
  'business.google.com',
] as const;

export const isMarketplaceHost = (host: string): boolean => {
  const h = host.toLowerCase().replace(/^www\./, '');
  return MARKETPLACE_HOSTS.some((m) => h === m || h.endsWith(`.${m}`));
};

/**
 * What the person typed in the website box, made into an address, or the reason
 * it is not one: a phone number or a handle is kept as a fact and never read.
 */
export const normaliseWebsiteInput = (
  text: string,
): { readonly kind: 'url'; readonly url: string; readonly host: string } | { readonly kind: 'phone' | 'handle' | 'not_a_website'; readonly text: string } => {
  const raw = text.trim();
  if (/^@[\w.]{2,}$/.test(raw)) return { kind: 'handle', text: raw };
  if (/^\+?[\d\s()-]{8,}$/.test(raw)) return { kind: 'phone', text: raw };
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;
  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    return { kind: 'not_a_website', text: raw };
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return { kind: 'not_a_website', text: raw };
  const host = parsed.hostname.toLowerCase();
  // `localhost` with a port is let through so a test can type its fixture site; the server reads it only in tests.
  const local = host === 'localhost' && parsed.port !== '';
  if ((!host.includes('.') && !local) || host.endsWith('.')) return { kind: 'not_a_website', text: raw };
  parsed.hash = '';
  return { kind: 'url', url: parsed.toString(), host };
};
